import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const VIDEO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_a5002346-e592-487d-94dc-dd0ae58d1921/artifacts/fkw6h25o_santacruz%2060-MINUTE%20COUPLE%20SESSION%20done%203999%20%281%29.mp4";

export default function Hero() {
  return (
    <section data-testid={TEST_IDS.heroSection} className="relative pt-20 pb-16 sm:pb-20 overflow-hidden">
      {/* Radial background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,#3b2410_0,#0d0b09_45%)]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center pt-8 sm:pt-12">
          {/* Left — Copy */}
          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
              className="flex items-center gap-2 mb-5">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-extrabold text-[#f2cf8b]">
                <span className="w-[7px] h-[7px] rounded-full bg-[#d7a85c]" />
                Limited Couple Experience
              </span>
            </motion.div>

            <h1 className="font-serif font-semibold text-[#f8f0df] leading-[0.93] tracking-tight mb-5">
              <span className="block text-[clamp(44px,7vw,84px)] line-reveal">IGNITE</span>
              <span className="block text-[clamp(44px,7vw,84px)] line-reveal line-reveal-delay-1">
                <em className="gold-gradient-text italic">LOVE.</em>
              </span>
              <span className="block text-[clamp(44px,7vw,84px)] line-reveal line-reveal-delay-2">RESET TOGETHER.</span>
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="text-base sm:text-lg text-[#bdb3a4] leading-relaxed max-w-[650px] mb-6">
              Step away from the noise and give yourselves 60 uninterrupted minutes
              to unwind, reconnect and indulge at Epsom — Mumbai's premium wellness sanctuary.
            </motion.p>

            {/* Offer pills */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="flex flex-wrap gap-2 mb-6">
              {["60-Minute Couple Session", "Premium Wellness Setting", "Only 20 Slots"].map((pill) => (
                <span key={pill} className="px-3 py-2 rounded-full text-[13px] text-[#ded4c4] border border-[rgba(242,207,139,0.18)] bg-[rgba(255,255,255,0.035)]">
                  {pill}
                </span>
              ))}
            </motion.div>

            {/* Price + CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-5">
              <div className="flex items-baseline gap-3">
                <span className="text-[54px] leading-none font-black text-[#f8f0df] tracking-tight">₹3,999</span>
                <span className="text-[17px] font-bold text-[#bdb3a4]">+ 5% GST</span>
              </div>
              <span className="px-4 py-2 rounded-full border border-[#d7a85c] text-[#f2cf8b] text-xs font-extrabold tracking-wider">
                ₹5 REFUNDABLE VERIFICATION
              </span>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}>
              <a href="#booking" data-testid={TEST_IDS.heroBookBtn}
                className="group inline-flex items-center justify-center gap-3 w-full max-w-[430px] py-[17px] px-[22px] rounded-xl bg-gradient-to-br from-[#f1cb82] to-[#b77a31] text-[#1b1108] font-black text-[17px] shadow-[0_14px_35px_rgba(215,168,92,0.18)] hover:shadow-xl transition-all duration-300">
                RESERVE MY COUPLE SESSION
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <p className="text-xs text-[#9f968a] mt-3 max-w-[430px] leading-relaxed">
                Pay only ₹5 today to verify your booking request. Refundable as per the offer terms. No hidden checkout fee.
              </p>
            </motion.div>
          </div>

          {/* Right — Creative / Video */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
            className="relative rounded-[26px] overflow-hidden border border-[rgba(242,207,139,0.18)] shadow-[0_24px_80px_rgba(0,0,0,0.45)] bg-[#24170d]">
            <video data-testid={TEST_IDS.heroVideo} autoPlay muted loop playsInline className="w-full h-auto aspect-[4/5] sm:aspect-[3/4] object-cover"
              poster="/assets/couple-reset-offer.png">
              <source src={VIDEO_URL} type="video/mp4" />
            </video>
            <div className="absolute right-3 top-3 px-3 py-2 rounded-full bg-[#111] border border-[rgba(255,255,255,0.2)] text-white text-xs font-black">
              ₹5 VERIFICATION
            </div>
            {/* Bottom overlay info */}
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-[#0d0b09]/95 to-transparent">
              <div className="inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0d0b09]/80 backdrop-blur-sm border border-[rgba(242,207,139,0.18)]">
                <span className="text-[#f8f0df] font-serif text-lg font-semibold">60 min</span>
                <span className="text-[#bdb3a4] text-xs uppercase tracking-wider">Just the two of you</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
